/* RESPONSIBLE TEAM: team-reporting */

/**
 * Builds a structured chart context and base64-encodes it for passing
 * to the Reporting Agent via the `?context=` URL query parameter.
 *
 * The context is an array of typed items — currently only 'chart' is supported.
 * The agent receives this structured data in the request body and can use it
 * to reproduce the exact reporting query (metric IDs, filters, date range, etc.).
 */

interface SerializedFilter {
  type: string;
  data: {
    property: string;
    attribute: string;
    values?: unknown[];
  };
}

interface SerializedLogicalFilter {
  type: 'and' | 'or';
  filters: SerializedFilter[];
}

interface SerializedSeries {
  metricId: string;
  datasetId: string | null;
  label: string | null;
  aggregation: string | null;
  percentileValue: number | null;
  filters: SerializedLogicalFilter | null;
}

interface ChartContextItem {
  type: 'chart';
  title: string | null;
  contextKey?: string;
  series: SerializedSeries[];
  dateRange: {
    selection: string | null;
    start: string | null;
    end: string | null;
  };
  viewBy: string | null;
  viewByTimeInterval: string | null;
  segmentBy: string | null;
  segmentByTimeInterval: string | null;
  visualizationType: string | null;
  // Populated at message-send time on the React side from the
  // rendered-values registry. Not produced by this serializer.
  // See intercom/intercom#511390.
  renderedValues?: ChartRenderedValue[];
}

type ChartRenderedValue = ChartRenderedCounterValue;

interface ChartRenderedCounterValue {
  type: 'counter';
  value: number | null;
  previousValue: number | null;
  numerator: number | null;
  denominator: number | null;
  target: number | null;
  unit: string | null;
  changeDirection: 'up' | 'down' | 'none';
  absoluteChange: number | null;
  improvementDirection: 'increasing' | 'decreasing';
  formattedValue: string;
  formattedDataLabel: string | null;
}

export interface ReportContextItem {
  type: 'report';
  title: string | null;
  contextKey?: string;
  charts: ChartContextItem[];
}

export type ContextItem = ChartContextItem | ReportContextItem;

function serializeFilters(logicalFilter: any): SerializedLogicalFilter | null {
  if (!logicalFilter?.filters?.length) {
    return null;
  }
  return {
    type: logicalFilter.type,
    filters: logicalFilter.filters.map((f: any) => ({
      type: f.type,
      data: {
        property: f.data.property,
        attribute: f.data.attribute,
        ...(f.data.values !== undefined ? { values: f.data.values } : {}),
      },
    })),
  };
}

function serializeDateRange(dateRange: any): ChartContextItem['dateRange'] {
  let selection = dateRange?.selection ?? dateRange?.selectedRange ?? null;
  let start: string | null = null;
  let end: string | null = null;

  if (dateRange?.start) {
    start = new Date(dateRange.start).toISOString();
  }
  if (dateRange?.end) {
    end = new Date(dateRange.end).toISOString();
  }

  return { selection, start, end };
}

export function buildChartContextItem(chart: any, reportState: any): ChartContextItem {
  let series = (chart.chartSeries?.toArray?.() ?? []).map((s: any) => ({
    metricId: s.metricId,
    datasetId: s.metric?.datasetId ?? null,
    label: s.label ?? null,
    aggregation: s.aggregation ?? null,
    percentileValue: s.percentileValue ?? null,
    filters: serializeFilters(s.filters),
  }));

  let dateRange = serializeDateRange(reportState.dateRange || chart.dateRange);

  return {
    type: 'chart',
    contextKey: series.map((s: { metricId: string }) => s.metricId).join(','),
    title: chart.title || chart.description || null,
    series,
    dateRange,
    viewBy: chart.viewBy ?? null,
    viewByTimeInterval: chart.viewByTimeInterval ?? null,
    segmentBy: chart.segmentBy ?? null,
    segmentByTimeInterval: chart.segmentByTimeInterval ?? null,
    visualizationType: chart.visualizationType ?? null,
  };
}

export function buildReportContextItem(report: any, reportState: any): ReportContextItem {
  let charts = (report.charts?.toArray?.() ?? []).map((chart: any) =>
    buildChartContextItem(chart, reportState),
  );
  return {
    type: 'report',
    title: report.title || report.titleForDisplay || null,
    contextKey: (report.title || report.titleForDisplay || 'untitled') as string,
    charts,
  };
}

export function buildEncodedChartContext(chart: any, reportState: any): string {
  return btoa(JSON.stringify([buildChartContextItem(chart, reportState)]));
}
